import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

function StatCard({ label, value, testId }) {
  return (
    <div data-testid={testId} className="border border-[#0A1628]/10 bg-white p-4">
      <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-1">{label}</div>
      <div className="font-serif text-2xl font-bold text-[#0A1628]">{value}</div>
    </div>
  );
}

function DailyChart({ daily }) {
  const max = Math.max(1, ...daily.map(d => d.views));
  return (
    <div data-testid="analytics-daily-chart" className="border border-[#0A1628]/10 bg-white p-4 mb-6">
      <div className="text-xs text-[#2D3142]/50 uppercase tracking-widest mb-3">Views per day</div>
      <div className="flex items-end gap-px h-28">
        {daily.map(d => (
          <div
            key={d.date}
            title={`${d.date}: ${d.views} view${d.views !== 1 ? "s" : ""}`}
            className="flex-1 bg-[#0A1628] hover:bg-[#B8860B] transition-colors min-w-[2px]"
            style={{ height: `${Math.max(2, (d.views / max) * 100)}%` }}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-[#2D3142]/40 mt-2">
        <span>{daily[0]?.date}</span>
        <span>{daily[daily.length - 1]?.date}</span>
      </div>
    </div>
  );
}

export const AnalyticsTab = ({ token }) => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics?days=${days}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, token]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 data-testid="analytics-title" className="font-serif text-2xl font-bold text-[#0A1628]">Page Views</h1>
          <span className="text-[#2D3142]/50 text-sm">Visits to each page over the selected period</span>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map(r => (
            <button
              key={r.days}
              data-testid={`analytics-range-${r.days}`}
              onClick={() => setDays(r.days)}
              className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${
                days === r.days
                  ? "bg-[#0A1628] text-[#F5F0E8] border-[#0A1628]"
                  : "border-[#0A1628]/20 text-[#2D3142] hover:border-[#0A1628]"
              }`}
            >
              {r.label}
            </button>
          ))}
          <Button
            variant="outline"
            size="sm"
            data-testid="analytics-refresh-btn"
            onClick={load}
            disabled={loading}
            className="rounded-none border-[#0A1628]/20 gap-2"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <StatCard label={`Total views · ${days} days`} value={data.total} testId="analytics-total" />
            <StatCard label="Views today" value={data.today} testId="analytics-today" />
            <StatCard label="Pages visited" value={data.pages.length} testId="analytics-pages-count" />
          </div>

          <DailyChart daily={data.daily} />

          <div className="border border-[#0A1628]/10 bg-white">
            <Table data-testid="analytics-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Page</TableHead>
                  <TableHead className="text-right">Views</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">Share</TableHead>
                  <TableHead className="hidden md:table-cell">Last viewed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.pages.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} data-testid="analytics-empty" className="text-center text-[#2D3142]/50 py-10">
                      No page views recorded yet in this period.
                    </TableCell>
                  </TableRow>
                )}
                {data.pages.map(p => (
                  <TableRow key={p.path} data-testid={`analytics-row-${p.path.replace(/\//g, "-") || "root"}`}>
                    <TableCell className="font-mono text-sm text-[#0A1628]">{p.path}</TableCell>
                    <TableCell className="text-right font-semibold">{p.views}</TableCell>
                    <TableCell className="text-right hidden sm:table-cell text-[#2D3142]/60">
                      {data.total ? Math.round((p.views / data.total) * 100) : 0}%
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-[#2D3142]/60 text-sm">
                      {new Date(p.last_view).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </>
  );
};
